import { useMemo } from 'react';
import DateCard from '../../../LastMatches/Modal/Content/DateCard';
import LastMatchesContent from '../../../LastMatches/Modal/Content';
import { useBetsMe } from '@/api/bets';
import LastBetChip from '../../Chips/Chip';
import { useSearchParams } from '@/hooks/useSearchParams';

import styles from './styles.module.scss';
import { ErrorCard, NoDataComponent } from '@starsoft/common/components';
import { GameIDType } from '@/enums/gameId';
import { parseToBigInt } from '@starsoft/common/utils/parsers';

export default function LastBetsModalContent(): JSX.Element {
  const search = useSearchParams();
  const gameId = parseToBigInt(search.get('game-id') as string) as GameIDType;
  const {
    data: bets,
    isPending,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error,
    refetch,
  } = useBetsMe({
    type: 'infinity',
    gameId: parseToBigInt(gameId) as GameIDType,
    limit: 50,
  });

  const Bets = useMemo(
    () =>
      bets?.map(bet => (
        <DateCard
          key={`last-bets-modal-card-${bet.id}`}
          createdAt={bet?.createdAt}
        >
          <LastBetChip bet={bet} cover gameId={gameId as GameIDType} />
        </DateCard>
      )),
    [bets, gameId],
  );

  const Skeletons = useMemo(
    () =>
      Array.from({ length: 50 }).map((_, i) => (
        <DateCard key={`last-bets-modal-card-skeleton-${i}`} isLoading>
          <LastBetChip isLoading cover gameId={gameId} />
        </DateCard>
      )),
    [gameId],
  );

  if (error) {
    return <ErrorCard error={error} refetch={refetch} />;
  }

  if (bets?.length == 0) {
    return (
      <div className={styles.container}>
        <NoDataComponent />
      </div>
    );
  }

  return (
    <LastMatchesContent
      hasNextPage={hasNextPage}
      isLoading={isPending || isFetchingNextPage}
      loadMore={fetchNextPage}
    >
      {Bets}
      {(isPending || isFetchingNextPage) && Skeletons}
    </LastMatchesContent>
  );
}
